import axios from "axios";
import { RedirectKeys } from "@entrilia/shared/auth/authTypes";
import { authState } from "@entrilia/shared/auth/b2c/redirects";
import { logError } from "@entrilia/shared/logging";
import { authCookieExists } from "@entrilia/shared/utilities/cookies";
import { parseJsonObject } from "@entrilia/shared/utilities/objectHelper";
import { pageRoutes } from "../routes";
import { setAuthMode } from "../variables";

export const impersonationStateParams = {
  impersonation: "impersonation",
  client: "client",
  user: "user",
  target: "target",
  fund: "fund",
};

interface ImpersonationState {
  impersonation?: boolean;
  user?: string;
  client?: string;
  target?: string;
}

let impersonationInterceptor: number | undefined;

export const restoreImpersonationSearchParams = (impersonationSerachParams: URLSearchParams) => {
  if (impersonationSerachParams.has(impersonationStateParams.impersonation)) {
    window.history.replaceState({}, "", `${window.location.pathname}?${impersonationSerachParams.toString()}`);
  }
};

export const cancelImpersonation = () => {
  if (impersonationInterceptor !== undefined) {
    axios.interceptors.request.eject(impersonationInterceptor);
  }

  setAuthMode("end_user");

  delete authState[impersonationStateParams.impersonation];
  delete authState[impersonationStateParams.client];
  delete authState[impersonationStateParams.user];
  delete authState[impersonationStateParams.target];
  delete authState[impersonationStateParams.fund];
};

export const viewAs = (email: string, fund: string) => {
  const getPathName = () => {
    const { pathname } = window.location;
    const segments = pathname.split("/");
    const pageName = segments[2]?.toLowerCase();

    if (pageName === pageRoutes.inbox || pageName === pageRoutes.fundraising) {
      return segments.slice(0, 3).join("/"); // Remove inbox filters or fundraising details page
    }

    return pathname;
  };

  const reloadUrlParams = new URLSearchParams(window.location.search);
  reloadUrlParams.set(impersonationStateParams.impersonation, "true");
  reloadUrlParams.set(impersonationStateParams.user, email);
  if (fund) {
    reloadUrlParams.set(impersonationStateParams.fund, fund);
  }

  window.location.href = `${window.location.origin}${getPathName()}?${reloadUrlParams.toString()}`;
};

export const handlePortalImpersonation = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasImpersonationParam = !!urlParams.get(impersonationStateParams.impersonation);
  const userParam = urlParams.get(impersonationStateParams.user);
  const targetParam = urlParams.get(impersonationStateParams.target);
  const fundParam = urlParams.get(impersonationStateParams.fund);
  const clientParam = (window.location.pathname ? window.location.pathname.slice(1) : "").split("/")[0];
  const isImpersonationRedirect = hasImpersonationParam && userParam && clientParam;

  if (isImpersonationRedirect) {
    impersonationInterceptor = addImpersonationInterceptor(clientParam, userParam, targetParam ?? "Investor");
    setAuthMode("impersonation");

    if (authCookieExists()) {
      return;
    }

    authState[impersonationStateParams.impersonation] = true;
    authState[impersonationStateParams.client] = clientParam;
    authState[impersonationStateParams.user] = userParam;
    authState[impersonationStateParams.target] = targetParam ?? "Investor";
    if (fundParam) {
      authState[impersonationStateParams.fund] = fundParam;
    }

    return;
  }

  const stateParam = urlParams.get(RedirectKeys.State);
  if (!stateParam) {
    setAuthMode("end_user");
    return;
  }

  const state = parseJsonObject<ImpersonationState>(stateParam);
  if (!state || !state.impersonation) {
    setAuthMode("end_user");
    return;
  }

  const { client, user, target } = state;
  if (client && user) {
    impersonationInterceptor = addImpersonationInterceptor(client, user, target ?? "Investor");
    setAuthMode("impersonation");
  } else {
    logError(`Invalid impersonation 'state' URL param: ${stateParam}`, "[impersonationHandler]");
    setAuthMode("end_user");
  }
};

const addImpersonationInterceptor = (client: string, user: string, impersonationTarget: string): number => {
  return axios.interceptors.request.use((requestConfig) => {
    const impersonationHeaders = createImpersonationHeaders(client, user, impersonationTarget);
    impersonationHeaders.forEach(([key, value]) => {
      requestConfig.headers[key] = value;
    });
    return requestConfig;
  });
};

export const setImpersonationHeaders = (clientCode: string, userEmail: string, impersonationTarget: string) => {
  if (impersonationInterceptor !== undefined) {
    axios.interceptors.request.eject(impersonationInterceptor);
  }

  impersonationInterceptor = addImpersonationInterceptor(clientCode, userEmail, impersonationTarget);
};

export const createImpersonationHeaders = (
  clientCode: string,
  userEmail: string,
  impersonationTarget: string
): Array<[string, string]> => [
  ["x-entrilia-onBehalfOfClient", encodeURIComponent(clientCode)],
  ["x-entrilia-onBehalfOfUser", encodeURIComponent(userEmail)],
  ["x-entrilia-impersonationTarget", encodeURIComponent(impersonationTarget)],
];
